import { useState } from "preact/hooks";
import { pingTier } from "../../../hud/format";
import { lobbyAddBot, lobbyBotLevel, lobbyKick, lobbyRemoveBot, lobbySelectTeam, teamIsFull } from "../../../game/lobby";
import type { LobbyPlayer, LobbyView } from "../../../game/lobby";
import { teamName } from "../../../game/hudBridge";
import { hudMode } from "../../../game/convert";
import { Badge } from "../../components/Badge";
import type { BotLevel } from "../../../hud/types";
import { Button } from "../../components/Button";
import { Segmented } from "../../components/Segmented";
import { cx } from "../../cx";

const TEAM_LABEL = { a: "Orange", b: "Blue" } as const;

const BOT_LEVELS = [
  { value: "easy", label: "Easy" },
  { value: "normal", label: "Normal" },
  { value: "hard", label: "Hard" },
] as const;

/** The host's bot controls of one team (or of the free-for-all list): a difficulty and an add button. */
function AddBot({ view, team }: { view: LobbyView; team: "a" | "b" | "none" }) {
  if (!view.isHost) return null;
  const full = !view.canAddBots || (team !== "none" && view.teamCounts[team] >= view.teamSize);
  return (
    <div class="lobby-addbot">
      <Segmented<BotLevel>
        ariaLabel="Bot difficulty"
        value={lobbyBotLevel.value}
        options={BOT_LEVELS}
        onChange={(v) => (lobbyBotLevel.value = v)}
      />
      <Button size="sm" variant="secondary" disabled={full} onClick={() => lobbyAddBot(team, lobbyBotLevel.value)}>
        {full ? "Full" : "Add bot"}
      </Button>
    </div>
  );
}

function PlayerRow({
  player,
  canKick,
  confirming,
  onAsk,
  onCancel,
  onConfirm,
}: {
  player: LobbyPlayer;
  canKick: boolean;
  confirming: boolean;
  onAsk(): void;
  onCancel(): void;
  onConfirm(): void;
}) {
  const tier = player.pingMs === null ? undefined : String(pingTier(player.pingMs));
  return (
    <li class={cx("lobby-row", player.isMe && "is-me")}>
      <span class="lobby-row__name" title={player.name}>
        {player.name}
      </span>
      {player.bot !== null && (
        <Badge tone="ink" class="lobby-row__bot">
          BOT {player.bot.toUpperCase()}
        </Badge>
      )}
      {player.isHost && <Badge tone="gold">Host</Badge>}
      {player.isMe && <span class="lobby-row__you">You</span>}
      <span class="lobby-row__lvl">
        <span class="lobby-row__lvl-k">LVL</span> <span class="lcd">{player.level}</span>
      </span>
      <span class="lobby-row__ping" data-tier={tier} title="Ping">
        {player.pingMs === null ? (
          <span class="muted">-</span>
        ) : (
          <>
            <span class="lcd">{player.pingMs}</span>
            <span class="lobby-row__unit">ms</span>
          </>
        )}
      </span>
      {canKick && player.bot !== null && (
        <Button size="sm" variant="ghost" class="lobby-row__kick" onClick={onConfirm} aria-label={`Remove ${player.name}`}>
          Remove
        </Button>
      )}
      {canKick &&
        player.bot === null &&
        (confirming ? (
          <span class="lobby-row__confirm">
            <Button size="sm" variant="danger" onClick={onConfirm} aria-label={`Kick ${player.name}`}>
              Kick
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel}>
              No
            </Button>
          </span>
        ) : (
          <Button size="sm" variant="ghost" class="lobby-row__kick" onClick={onAsk} aria-label={`Kick ${player.name}`}>
            Kick
          </Button>
        ))}
    </li>
  );
}

/** Players by team (team modes) or one list (free for all), with the team pick and, for the host, kick. */
export function Roster({ view }: { view: LobbyView }) {
  const [asking, setAsking] = useState<number | null>(null);
  const rows = (list: LobbyPlayer[]) =>
    list.map((p) => (
      <PlayerRow
        key={p.id}
        player={p}
        canKick={view.isHost && !p.isMe}
        confirming={asking === p.id}
        onAsk={() => setAsking(p.id)}
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null);
          if (p.bot !== null) lobbyRemoveBot(p.id);
          else lobbyKick(p.id);
        }}
      />
    ));

  if (!view.teamMode) {
    return (
      <div class="lobby-teams">
        <section class="lobby-team" aria-label="Players">
          <header class="lobby-team__head">
            <span class="lobby-team__name">Players</span>
            <span class="lobby-team__count">
              <span class="lcd">{view.players.length}</span>
              <span class="lobby-team__of">/{view.rules.maxPlayers}</span>
            </span>
          </header>
          <ul class="lobby-roster">{rows(view.players)}</ul>
          <AddBot view={view} team="none" />
          <p class="lobby-note">Free for all: there are no teams to pick.</p>
        </section>
      </div>
    );
  }

  const mode = hudMode(view.rules.mode);
  return (
    <div class="lobby-teams">
      {(["a", "b"] as const).map((team) => {
        const members = view.players.filter((p) => p.team === team);
        const mine = members.some((p) => p.isMe);
        const full = teamIsFull(view, team);
        const label = TEAM_LABEL[team];
        return (
          <section key={team} class="lobby-team" data-team={team} aria-label={`Team ${label}`}>
            <header class="lobby-team__head">
              <span class="lobby-team__name">{label}</span>
              <span class="lobby-team__role">{mode === "defusal" ? teamName(mode, team) : ""}</span>
              <span class="lobby-team__count">
                <span class="lcd">{members.length}</span>
                <span class="lobby-team__of">/{view.teamSize}</span>
              </span>
            </header>
            <ul class="lobby-roster">{members.length > 0 ? rows(members) : <li class="lobby-row lobby-row--empty">Nobody yet</li>}</ul>
            <AddBot view={view} team={team} />
            {view.canPickTeam && (
              <Button
                size="sm"
                class="lobby-team__join"
                variant={mine ? "ghost" : "secondary"}
                disabled={mine || full}
                onClick={() => lobbySelectTeam(team)}
              >
                {mine ? "You are on this team" : full ? "Team full" : `Join ${label}`}
              </Button>
            )}
          </section>
        );
      })}
    </div>
  );
}
